import React, { useState } from "react";
import { FiX, FiUserPlus, FiUsers, FiUserCheck } from "react-icons/fi";
import Select from "react-select";
import "./AddPersonFromEmailModal.css";

const AddPersonFromEmailModal = ({ email, onClose, onAdd, showToast, users = [], currentUser }) => {
  function extractEmail(headerText) {
    if (!headerText) return "";
    const match = headerText.match(/<([^>]+)>/);
    return match ? match[1].trim() : headerText.trim();
  }

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    status: "New",
  });
  const [primaryUsers, setPrimaryUsers] = useState(currentUser ? [currentUser.id] : []);
  const [secondaryUsers, setSecondaryUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [initialized, setInitialized] = useState(false);

  // Set initial values only once when modal opens
  React.useEffect(() => {
    if (email && !initialized) {
      const emailAddress = email?.email || extractEmail(email?.from || email?.to || "");
      const defaultName = email?.name || "";
      setFormData({
        name: defaultName,
        email: emailAddress,
        company: "",
        phone: "",
        status: "New",
      });
      setInitialized(true);
    }
  }, [email, initialized]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email) {
      showToast?.("Name and email are required", "error");
      return;
    }

    setSaving(true);
    try {
      console.log("[AddPersonFromEmailModal] Submitting person data:", formData);
      const dataWithAssignments = {
        ...formData,
        assignments: {
          primary: primaryUsers,
          secondary: secondaryUsers
        }
      };
      await onAdd(dataWithAssignments);
      console.log("[AddPersonFromEmailModal] Person added successfully");
      onClose();
    } catch (error) {
      console.error("[AddPersonFromEmailModal] Error adding person:", error);
      // Error toast is handled in the parent component
    } finally {
      setSaving(false);
    }
  };

  // Format users for react-select
  const userOptions = users.map(user => ({
    value: user.id,
    label: `${user.first_name} ${user.last_name}`,
    color: user.chip_color
  }));

  const customStyles = {
    option: (provided, state) => ({
      ...provided,
      display: 'flex',
      alignItems: 'center',
    }),
    multiValue: (provided, state) => ({
      ...provided,
      backgroundColor: state.data.color || '#e0e0e0',
    }),
  };

  if (!email) return null;

  return (
    <div className="add-person-modal-overlay" onClick={onClose}>
      <div className="add-person-modal" onClick={(e) => e.stopPropagation()}>
        <div className="add-person-modal-header">
          <h3>Add to People</h3>
          <button className="close-button" onClick={onClose} title="Close">
            <FiX size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="add-person-modal-body">
            <div className="form-field">
              <label>Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Full Name"
                disabled={saving}
                required
              />
            </div>

            <div className="form-field">
              <label>Email *</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="email@example.com"
                disabled={saving}
                required
              />
            </div>

            <div className="form-field">
              <label>Company</label>
              <input
                type="text"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                placeholder="Company Name"
                disabled={saving}
              />
            </div>

            <div className="form-field">
              <label>Phone</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="(555) 123-4567"
                disabled={saving}
              />
            </div>

            <div className="form-field">
              <label>Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                disabled={saving}
              >
                <option value="New">New</option>
                <option value="Contacted">Contacted</option>
                <option value="Scheduled">Scheduled</option>
                <option value="Qualified">Qualified</option>
                <option value="Not Interested">Not Interested</option>
              </select>
            </div>

            <div className="form-field">
              <label>
                <FiUserCheck size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                Primary Users
              </label>
              <Select
                isMulti
                options={userOptions}
                value={userOptions.filter(opt => primaryUsers.includes(opt.value))}
                onChange={(selected) => setPrimaryUsers(selected ? selected.map(s => s.value) : [])}
                styles={customStyles}
                placeholder="Select primary users..."
                isDisabled={saving}
              />
            </div>

            <div className="form-field">
              <label>
                <FiUsers size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                Secondary Users
              </label>
              <Select
                isMulti
                options={userOptions}
                value={userOptions.filter(opt => secondaryUsers.includes(opt.value))}
                onChange={(selected) => setSecondaryUsers(selected ? selected.map(s => s.value) : [])}
                styles={customStyles}
                placeholder="Select secondary users..."
                isDisabled={saving}
              />
            </div>
          </div>

          <div className="add-person-modal-footer">
            <button type="button" className="cancel-button" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="add-button" disabled={saving}>
              <FiUserPlus /> {saving ? "Adding..." : "Add Person"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddPersonFromEmailModal;

